import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { REPORT_OFFERS_LIST_QUERY, REPORT_OFFERS_LIST_COUNT_QUERY } from '../../gql';
import { ReportWLOfferListViewFragment } from '__generated__/graphql';
import { useReportFilters, useReportSort } from './useReportFilters';
import { getGuestsStatistics } from '../utils/countHelper';
import { formatDate } from 'components/DateTime';
import { NO_PRICE } from 'semshared/pricelist/quickprice';

export const tableHeaders = [
  { field: '_createdAt', sortField: 'requestDate', label: 'Anfragedatum' },
  { field: 'offerCode', sortField: 'refCode', label: 'Anfrage-ID' },
  { field: 'status', sortField: 'offerState', label: 'Anfragestatus' },
  { field: 'latestVersionReport.status', label: 'Detailstatus' },
  { field: 'booking.refCode', sortField: 'bookingRefCode', label: 'Buchungs-ID' },
  { field: 'booking.status', sortField: 'bookingState', label: 'Buchungsstatus' },
  { field: 'booking.title', sortField: 'bookingTitle', label: 'Seminarname' },
  { field: 'publicCompany.name', sortField: 'clientCompanyName', label: 'Firma' },
  { field: 'publicCompany.id', label: 'Firma-ID' },
  { field: 'publicCompany.country', sortField: 'clientCountry', label: 'Land' },
  { field: '_client', sortField: 'clientName', label: 'Buchender' },
  { field: '_clientId', label: 'Buchender-ID' },
  { field: 'requestedBy', label: 'Angefragt durch' },
  { field: '_clientEmail', label: 'E-Mail' },
  { field: '_seminarStartDate', skipTable: true, label: 'Seminar von' },
  { field: '_seminarEndDate', skipTable: true, label: 'Seminar bis' },
  { field: '_seminarDate', skipCsv: true, label: 'Seminardatum' },
  { field: 'hotel.name', sortField: 'hotelName', label: 'Hotel' },
  { field: '_singleRooms', label: 'Einzelzimmer' },
  { field: '_doubleRooms', label: 'Doppelzimmer' },
  { field: '_guests', label: 'Seminarteilnehmer' },
  { field: 'latestVersion.totalPriceNet', label: 'Buchungsbetrag Netto' },
  { field: 'latestVersion.totalPriceGross', label: 'Buchungsbetrag Brutto' },
  { field: 'commissionAmountNet', label: 'Kommission Netto' },
  { field: '_ivApprovedAt', label: 'Freigabe' },
];
export const tableData = (results: ReportWLOfferListViewFragment[]) =>
  results.map(r => {
    const { maxGuests, maxRoomsEZ, maxRoomsDZ, startDate, endDate } = getGuestsStatistics(r.input, r.inputLister);

    return {
      ...r,
      _createdAt: r.createdAt ? formatDate(new Date(r.createdAt)) : null,
      _clientId: r.booking?.publicClient ? r.booking.publicClient.id : r.client ? r.client.id : null,
      _client: r.booking?.publicClient
        ? `${r.booking?.publicClient.lastname} ${r.booking?.publicClient.firstname}`
        : r.client
          ? `${r.client.lastname} ${r.client.firstname}`
          : null,
      _clientEmail: r.booking?.publicClient ? r.booking?.publicClient.email : r.client ? r.client.email : null,
      _seminarStartDate: r.input?.startDate
        ? formatDate(new Date(r.input?.startDate))
        : r.inputLister?.startDate
          ? formatDate(new Date(r.inputLister?.startDate))
          : null,
      _seminarEndDate: r.input?.endDate
        ? formatDate(new Date(r.input?.endDate))
        : r.inputLister?.endDate
          ? formatDate(new Date(r.inputLister?.endDate))
          : null,
      _seminarDate: `${r.input?.startDate ? formatDate(new Date(r.input?.startDate)) : r.inputLister?.startDate ? formatDate(new Date(r.inputLister?.startDate)) : null} - ${r.input?.endDate ? formatDate(new Date(r.input?.endDate)) : r.inputLister?.endDate ? formatDate(new Date(r.inputLister?.endDate)) : null}`,
      _singleRooms: maxRoomsEZ,
      _doubleRooms: maxRoomsDZ,
      _guests: maxGuests,
      _ivApprovedAt: r.ivApprovedAt
        ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(r.ivApprovedAt))
        : null,
    };
  });

const EXPORT_BATCH_SIZE = 500;

const normalizeMoneyValue = (value: number | null | undefined): number => {
  if (value == null) return 0;
  if (!Number.isFinite(value)) return 0;
  if (value === NO_PRICE) return 0;
  return value;
};

/**
 * Hook for managing report data fetching, pagination, and export.
 * Handles lazy queries for offers list and count with filter/sort state.
 */
export function useReportData() {
  const filters = useReportFilters();
  const sort = useReportSort();

  const [reportResults, setReportResults] = useState<ReportWLOfferListViewFragment[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 50,
    totalCount: 0,
  });
  const [allResultsTotals, setAllResultsTotals] = useState<{
    singleRooms: number;
    doubleRooms: number;
    guests: number;
    totalPriceNet: number;
    totalPriceGross: number;
    commissionAmountNet: number;
  } | null>(null);

  const [executeOffersQuery] = useLazyQuery(REPORT_OFFERS_LIST_QUERY);
  const [executeOffersCountQuery] = useLazyQuery(REPORT_OFFERS_LIST_COUNT_QUERY);

  const handleReload = () => {
    handleGenerateReport(pagination.page, pagination.rowsPerPage);
  };

  const handleChangePage = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    handleGenerateReport(newPage, pagination.rowsPerPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setPagination(prev => ({
      ...prev,
      page: 0,
      rowsPerPage: newRowsPerPage,
    }));
    handleGenerateReport(0, newRowsPerPage);
  };

  const handleGenerateReport = async (pageNumber = 0, pageSize = pagination.rowsPerPage) => {
    setReportLoading(true);
    setReportResults([]);
    setAllResultsTotals(null);

    try {
      const [offersResult, offersCountResult] = await Promise.all([
        executeOffersQuery({
          variables: {
            filters: filters,
            sort: sort,
            skip: pageNumber * pageSize,
            take: pageSize,
          },
          fetchPolicy: 'network-only',
        }),
        executeOffersCountQuery({
          variables: {
            filters: filters,
          },
          fetchPolicy: 'network-only',
        }),
      ]);

      setReportResults(offersResult.data?.reportOffersList || []);
      const pageResults = offersResult.data?.reportOffersList || [];
      const totalCount = offersCountResult.data?.reportOffersListCount || 0;
      setPagination(prev => ({
        ...prev,
        page: pageNumber,
        rowsPerPage: pageSize,
        totalCount,
      }));

      // Only calculate totals if we have results AND data on current page
      if (totalCount > 0 && pageResults.length > 0) {
        const allResults = await fetchAllReportResults();
        const allData = tableData(allResults);

        const totals = {
          singleRooms: allData.reduce((sum, r) => sum + (r._singleRooms || 0), 0),
          doubleRooms: allData.reduce((sum, r) => sum + (r._doubleRooms || 0), 0),
          guests: allData.reduce((sum, r) => sum + (r._guests || 0), 0),
          totalPriceNet: allData.reduce((sum, r) => sum + normalizeMoneyValue(r.latestVersion?.totalPriceNet), 0),
          totalPriceGross: allData.reduce((sum, r) => sum + normalizeMoneyValue(r.latestVersion?.totalPriceGross), 0),
          commissionAmountNet: allData.reduce((sum, r) => sum + normalizeMoneyValue(r.commissionAmountNet), 0),
        };

        setAllResultsTotals(totals);
      } else {
        // Ensure totals are cleared when no results
        setAllResultsTotals(null);
      }
    } catch (error) {
      console.error('Report query error:', error);
      setAllResultsTotals(null);
    } finally {
      setReportLoading(false);
    }
  };

  const fetchAllReportResults = useCallback(async () => {
    const allResults: ReportWLOfferListViewFragment[] = [];
    let skip = 0;
    let hasMore = true;

    // Log filters for debugging
    console.log('fetchAllReportResults: Starting export with filters:', JSON.stringify(filters));

    while (hasMore) {
      const { data, error } = await executeOffersQuery({
        variables: {
          filters,
          sort,
          skip,
          take: EXPORT_BATCH_SIZE,
        },
        fetchPolicy: 'network-only',
      });

      // Check for GraphQL errors
      if (error) {
        console.error('fetchAllReportResults: GraphQL error:', error);
        throw new Error(`Export query failed: ${error.message}`);
      }

      const chunk = data?.reportOffersList || [];
      console.log(`fetchAllReportResults: Fetched ${chunk.length} results (skip: ${skip})`);
      allResults.push(...chunk);

      if (chunk.length < EXPORT_BATCH_SIZE) {
        hasMore = false;
      } else {
        skip += EXPORT_BATCH_SIZE;
      }
    }

    console.log(`fetchAllReportResults: Total results: ${allResults.length}`);
    return allResults;
  }, [executeOffersQuery, filters, sort]);

  return {
    reportResults,
    reportLoading,
    pagination,
    allResultsTotals,
    handleGenerateReport,
    handleReload,
    handleChangePage,
    handleChangeRowsPerPage,
    setPagination,
    fetchAllReportResults,
    filters,
    sort,
  };
}
